import { t } from '../i18n';
import { useMemo } from 'react';
import { Plus, Repeat } from 'lucide-react';
import {
  dayOfMonthInCycle,
  parsePeriod,
  todayInTz,
  type CurrencyCode,
  type PaymentStateDto,
  type SubscriptionDto,
} from '@trestly/shared';
import { usePayments, useSetPaymentPaid, useSubscriptions, useUpdateSubscription } from '../api/hooks';
import { usePeriod } from '../period-context';
import { useToast } from '../components/Toast';
import { useSheetState } from '../components/useSheetState';
import { HeaderAction, ScreenHeader } from '../components/ScreenHeader';
import { PaymentSheet } from '../components/PaymentSheet';
import { SubscriptionSheet } from '../components/SubscriptionSheet';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { ListSkeleton } from '../components/Skeleton';
import { Checkbox, Toggle } from '../components/Toggle';
import { errorText } from '../lib/errors';
import { daysBetween, formatIsoDate } from '../lib/format';
import { currencySign, money, moneyLabel } from '../lib/money';
import { haptic } from '@/telegram';
import { cn } from '@/lib/utils';

const GROUPS = [
  { id: 'late', title: () => t('obligationsPage.late') },
  { id: 'week', title: () => t('obligationsPage.week') },
  { id: 'later', title: () => t('obligationsPage.later') },
  { id: 'done', title: () => t('obligationsPage.done') },
] as const;

type GroupId = (typeof GROUPS)[number]['id'];

/** Ближе этого срока — «на этой неделе». */
const WEEK_DAYS = 7;

/**
 * Обязательства периода: платежи по сроку и справочник подписок. Итог к оплате
 * — только платежи: подписки в нём уже учтены одной строкой-платежом
 * (PLAN.md, раздел 4), и их сложение дало бы двойной учёт.
 */
export function Obligations(): JSX.Element {
  const { period, cycleStartDay } = usePeriod();
  const payments = usePayments(period);
  const subscriptions = useSubscriptions();
  const setPaid = useSetPaymentPaid(period);
  const updateSubscription = useUpdateSubscription();
  const todayIso = todayInTz();
  const toast = useToast();

  /** Быстрые галки: при ошибке отметка откатится сама, но молча это выглядит как глюк. */
  const failed = (what: string) => (error: Error) => {
    haptic.result('error');
    toast.show({ text: `${what}: ${errorText(error)}` });
  };

  // null внутри — создание новой записи.
  const paymentSheet = useSheetState<{ payment: PaymentStateDto | null }>();
  const subscriptionSheet = useSheetState<{ subscription: SubscriptionDto | null }>();

  const grouped = useMemo(() => {
    const map = new Map<GroupId, PaymentStateDto[]>(GROUPS.map((group) => [group.id, []]));
    const sorted = [...(payments.data?.items ?? [])].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    for (const item of sorted) {
      const diff = daysBetween(todayIso, item.dueDate);
      const group: GroupId =
        item.paidAt !== null ? 'done' : diff < 0 ? 'late' : diff < WEEK_DAYS ? 'week' : 'later';
      map.get(group)?.push(item);
    }
    return map;
  }, [payments.data, todayIso]);

  const subscriptionRows = useMemo(() => {
    const key = parsePeriod(period);
    return (subscriptions.data?.items ?? [])
      .map((item) => ({ item, chargeDate: dayOfMonthInCycle(item.billingDay, key, cycleStartDay) }))
      .sort((a, b) => Number(b.item.active) - Number(a.item.active) || a.chargeDate.localeCompare(b.chargeDate));
  }, [subscriptions.data, period, cycleStartDay]);

  const data = payments.data;
  const lateCount = grouped.get('late')?.length ?? 0;

  return (
    <>
      <ScreenHeader
        title={t('obligationsPage.title')}
        action={
          <HeaderAction label={t('obligationsPage.newPayment')} onClick={() => paymentSheet.show({ payment: null })}>
            <Plus className="size-5" aria-hidden />
          </HeaderAction>
        }
      />

      <div className="px-4">
        {payments.isPending ? (
          <>
            <div className="mb-5 h-[124px] animate-pulse rounded-[26px] bg-muted" />
            <ListSkeleton rows={4} />
          </>
        ) : payments.isError ? (
          <ErrorState message={errorText(payments.error)} onRetry={() => void payments.refetch()} />
        ) : data ? (
          <>
            <Summary
              totalMinor={data.totalBaseMinor}
              paidMinor={data.paidBaseMinor}
              lateCount={lateCount}
              currency={data.baseCurrency}
            />

            {data.items.length === 0 ? (
              <div className="panel mb-5">
                <EmptyState
                  title={t('obligationsPage.emptyTitle')}
                  description={t('obligationsPage.emptyDesc')}
                />
              </div>
            ) : null}

            {GROUPS.map((group) => {
              const list = grouped.get(group.id) ?? [];
              if (list.length === 0) return null;
              const sum = list.reduce((total, item) => total + item.amountBaseMinor, 0);
              return (
                <section key={group.id} className="mb-4">
                  <div className="flex items-baseline justify-between px-1 pb-1.5">
                    <h2 className={cn('section-title', group.id === 'late' && 'text-destructive')}>
                      {group.title()} · {list.length}
                    </h2>
                    <span className="num text-[12.5px] text-faint">{money(sum, data.baseCurrency)}</span>
                  </div>
                  <ul className="panel overflow-hidden" style={{ '--sep': '56px' } as React.CSSProperties}>
                    {list.map((item) => (
                      <PaymentRow
                        key={item.paymentId}
                        item={item}
                        todayIso={todayIso}
                        baseCurrency={data.baseCurrency}
                        late={group.id === 'late'}
                        onToggle={(paid) => {
                          if (setPaid.isPending) return;
                          setPaid.mutate({ id: item.paymentId, paid }, { onError: failed(t('obligationsPage.markFailed')) });
                        }}
                        onOpen={() => paymentSheet.show({ payment: item })}
                      />
                    ))}
                  </ul>
                </section>
              );
            })}
          </>
        ) : null}

        <section className="mt-6 mb-4">
          <div className="flex items-center justify-between px-1 pb-1.5">
            <h2 className="section-title">
              {t('obligationsPage.subscriptions')}{subscriptions.data ? ` · ${t('obligationsPage.perMonth', { sum: money(subscriptions.data.totalBaseMinor, subscriptions.data.baseCurrency) })}` : ''}
            </h2>
            <button
              type="button"
              className="-mr-1 flex h-8 items-center gap-1 rounded-full px-2.5 text-[13px] font-semibold text-accent active:bg-accent-soft"
              onClick={() => {
                haptic.tap();
                subscriptionSheet.show({ subscription: null });
              }}
            >
              <Plus className="size-4" aria-hidden />
              {t('obligationsPage.subscriptionAdd')}
            </button>
          </div>
          <p className="px-1 pb-2 text-[12.5px] leading-snug text-faint">
            {t('obligationsPage.subscriptionsNote')}
          </p>
          {subscriptions.isPending ? (
            <ListSkeleton rows={3} />
          ) : subscriptions.isError ? (
            <ErrorState message={errorText(subscriptions.error)} onRetry={() => void subscriptions.refetch()} />
          ) : subscriptionRows.length === 0 ? (
            <div className="panel">
              <EmptyState title={t('obligationsPage.subsEmptyTitle')} description={t('obligationsPage.subsEmptyDesc')} />
            </div>
          ) : (
            <ul className="panel overflow-hidden" style={{ '--sep': '56px' } as React.CSSProperties}>
              {subscriptionRows.map(({ item, chargeDate }) => (
                <SubscriptionRow
                  key={item.id}
                  item={item}
                  chargeDate={chargeDate}
                  todayIso={todayIso}
                  baseCurrency={subscriptions.data?.baseCurrency ?? 'BYN'}
                  onOpen={() => subscriptionSheet.show({ subscription: item })}
                  onToggle={(active) => {
                    if (updateSubscription.isPending) return;
                    updateSubscription.mutate(
                      { id: item.id, patch: { active } },
                      { onSuccess: () => haptic.result('success'), onError: failed(t('obligationsPage.subFailed')) },
                    );
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </div>

      <PaymentSheet
        open={paymentSheet.open}
        payment={paymentSheet.value?.payment ?? null}
        period={period}
        onClose={paymentSheet.close}
      />
      <SubscriptionSheet
        open={subscriptionSheet.open}
        subscription={subscriptionSheet.value?.subscription ?? null}
        onClose={subscriptionSheet.close}
      />
    </>
  );
}

function Summary({
  totalMinor,
  paidMinor,
  lateCount,
  currency,
}: {
  totalMinor: number;
  paidMinor: number;
  lateCount: number;
  currency: CurrencyCode;
}): JSX.Element {
  const share = totalMinor > 0 ? Math.min(1, paidMinor / totalMinor) : 0;
  return (
    <div className="panel mb-6 px-5 py-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[12px] font-medium text-faint">{t('obligationsPage.leftToPay')}</div>
          <div className="num-hero text-[36px] leading-tight font-semibold">
            {money(totalMinor - paidMinor, currency)}{' '}
            <span className="text-[20px] font-semibold text-faint">{currencySign(currency)}</span>
          </div>
        </div>
        {lateCount > 0 ? (
          <span className="mb-1.5 rounded-full bg-destructive/14 px-2.5 py-1 text-[12.5px] font-semibold text-destructive">
            {t('obligationsPage.overdueCount', { n: lateCount })}
          </span>
        ) : null}
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-ok" style={{ width: `${Math.round(share * 100)}%` }} />
      </div>
      <div className="mt-1.5 flex justify-between text-[12px] text-faint">
        <span>
          {t('obligationsPage.paid')} <span className="num">{money(paidMinor, currency)}</span>
        </span>
        <span>
          {t('obligationsPage.total')} <span className="num">{money(totalMinor, currency)}</span>
        </span>
      </div>
    </div>
  );
}

/** «сегодня», «завтра», «3 дня назад», «25 сентября». */
function whenLabel(dueIso: string, todayIso: string): string {
  const diff = daysBetween(todayIso, dueIso);
  if (diff === 0) return t('obligationsPage.today');
  if (diff === 1) return t('obligationsPage.tomorrow');
  if (diff < 0) return t('obligationsPage.daysAgo', { n: -diff });
  return formatIsoDate(dueIso);
}

function Amounts({
  amountMinor,
  currency,
  baseMinor,
  baseCurrency,
}: {
  amountMinor: number;
  currency: CurrencyCode;
  baseMinor: number;
  baseCurrency: CurrencyCode;
}): JSX.Element {
  return (
    <div className="text-right">
      <div className="num text-[15px] font-semibold whitespace-nowrap">{moneyLabel(amountMinor, currency)}</div>
      {currency !== baseCurrency ? (
        <div className="num text-[11.5px] text-faint">≈ {money(baseMinor, baseCurrency)}</div>
      ) : null}
    </div>
  );
}

function PaymentRow({
  item,
  todayIso,
  baseCurrency,
  late,
  onToggle,
  onOpen,
}: {
  item: PaymentStateDto;
  todayIso: string;
  baseCurrency: CurrencyCode;
  late: boolean;
  onToggle: (paid: boolean) => void;
  onOpen: () => void;
}): JSX.Element {
  const paid = item.paidAt !== null;
  // Задана фактическая сумма — показываем её: итоги периода считаются по факту.
  const amountMinor = item.amountMinorActual ?? item.amountMinor;
  const actualNote = item.amountMinorActual !== null && item.amountMinorActual !== item.amountMinor
    ? ` · ${t('obligationsPage.plan', { sum: moneyLabel(item.amountMinor, item.currency) })}`
    : '';
  return (
    <li className="flex min-h-[56px] items-center gap-3 py-2 pl-4">
      <span className={cn(late && '[&>button]:border-destructive')}>
        <Checkbox checked={paid} onChange={onToggle} label={paid ? t('obligationsPage.unmark') : t('obligationsPage.mark')} />
      </span>
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-3 pr-4 text-left active:opacity-60"
        onClick={() => {
          haptic.tap();
          onOpen();
        }}
      >
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-[15px] font-medium', paid && 'text-muted-foreground')}>{item.name}</span>
          <span className={cn('block text-[12.5px] text-faint', late && 'text-destructive')}>
            {paid ? t('obligationsPage.paidUntil', { date: formatIsoDate(item.dueDate) }) : whenLabel(item.dueDate, todayIso)}
            {actualNote}
          </span>
        </span>
        <Amounts
          amountMinor={amountMinor}
          currency={item.currency}
          baseMinor={item.amountBaseMinor}
          baseCurrency={baseCurrency}
        />
      </button>
    </li>
  );
}

function SubscriptionRow({
  item,
  chargeDate,
  todayIso,
  baseCurrency,
  onOpen,
  onToggle,
}: {
  item: SubscriptionDto;
  chargeDate: string;
  todayIso: string;
  baseCurrency: CurrencyCode;
  onOpen: () => void;
  onToggle: (active: boolean) => void;
}): JSX.Element {
  const charged = chargeDate <= todayIso;
  return (
    <li className="flex min-h-[56px] items-center gap-3 py-2 pr-4">
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-3 pl-4 text-left active:opacity-60"
        onClick={() => {
          haptic.tap();
          onOpen();
        }}
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Repeat className="size-3.5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-[15px] font-medium', !item.active && 'text-muted-foreground')}>
            {item.name}
          </span>
          <span className="block text-[12.5px] text-faint">
            {!item.active
              ? t('obligationsPage.paused')
              : charged
                ? t('obligationsPage.charged', { date: formatIsoDate(chargeDate) })
                : t('obligationsPage.willCharge', { when: whenLabel(chargeDate, todayIso) })}
          </span>
        </span>
        <Amounts
          amountMinor={item.amountMinor}
          currency={item.currency}
          baseMinor={item.amountBaseMinor}
          baseCurrency={baseCurrency}
        />
      </button>
      <Toggle checked={item.active} label={t('obligationsPage.subActive', { name: item.name })} onChange={onToggle} />
    </li>
  );
}